// src/components/layout/CookieConsentBanner.tsx
// Floats at the bottom-right on desktop; full-width bar on mobile.
// Slides up from off-screen on mount. Stays visible until the user
// explicitly accepts or declines — implied consent is NOT enough (GDPR).

import { useEffect, useState }   from 'react';
import { useThemePalette }        from '../../hooks/useThemePalette';
import { useCookieConsentStore }  from '../../stores/useCookieConsentStore';
import { useLegalModalStore }     from '../../stores/useLegalModalStore';
import { useT }                   from '../../data/translations';

export function CookieConsentBanner() {
  const C       = useThemePalette();
  const t       = useT();
  const status  = useCookieConsentStore((s) => s.status);
  const accept  = useCookieConsentStore((s) => s.accept);
  const decline = useCookieConsentStore((s) => s.decline);
  const openCookiesModal = useLegalModalStore((s) => s.open);

  // Slide-up entrance via state-driven transform (no CSS files)
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (status !== null) return;
    const id = setTimeout(() => setEntered(true), 400);
    return () => clearTimeout(id);
  }, [status]);

  if (status !== null) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const containerStyle: React.CSSProperties = {
    position:             'fixed',
    boxSizing:            'border-box',
    bottom:               isMobile ? 0 : '1.5rem',
    left:                 isMobile ? 0 : 'auto',
    right:                isMobile ? 0 : '1.5rem',
    width:                isMobile ? '100%' : 'clamp(300px, 90vw, 420px)',
    maxWidth:             '100vw',
    zIndex:               9000,
    transform:            entered ? 'translateY(0)' : 'translateY(110%)',
    opacity:              entered ? 1 : 0,
    transition:           'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease',
    background:           C.frosted,
    backdropFilter:       'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border:               `1px solid ${C.borderH}`,
    borderRadius:         isMobile ? '16px 16px 0 0' : '16px',
    boxShadow:            '0 8px 32px rgba(0,0,0,0.24)',
    padding:              isMobile
      ? '1.125rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom, 0px))'
      : '1.25rem 1.5rem 1.5rem',
    display:              'flex',
    flexDirection:        'column',
    gap:                  '0.875rem',
  };

  const headerStyle: React.CSSProperties = {
    display:    'flex',
    alignItems: 'center',
    gap:        '0.5rem',
  };

  const titleStyle: React.CSSProperties = {
    fontSize:   '0.9375rem',
    fontWeight: 600,
    color:      C.text,
    margin:     0,
  };

  const bodyStyle: React.CSSProperties = {
    fontSize:     '0.8125rem',
    lineHeight:   1.55,
    color:        C.muted,
    margin:       0,
    overflowWrap: 'break-word',
  };

  const linkStyle: React.CSSProperties = {
    color:          C.accent,
    textDecoration: 'underline',
    cursor:         'pointer',
    background:     'none',
    border:         'none',
    padding:        0,
    font:           'inherit',
    fontSize:       'inherit',
  };

  const actionsStyle: React.CSSProperties = {
    display:       'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap:           '0.625rem',
    marginTop:     '0.125rem',
  };

  const acceptBtnStyle: React.CSSProperties = {
    flex:          1,
    padding:       '0.5625rem 0',
    borderRadius:  '8px',
    border:        'none',
    background:    C.accent,
    color:         '#fff',
    fontSize:      '0.875rem',
    fontWeight:    600,
    cursor:        'pointer',
    letterSpacing: '0.01em',
    transition:    'opacity 0.15s ease',
  };

  const declineBtnStyle: React.CSSProperties = {
    flex:         '0 0 auto',
    padding:      '0.5625rem 1rem',
    borderRadius: '8px',
    border:       `1px solid ${C.border}`,
    background:   'transparent',
    color:        C.muted,
    fontSize:     '0.875rem',
    fontWeight:   500,
    cursor:       'pointer',
    transition:   'border-color 0.15s ease, color 0.15s ease',
  };

  return (
    <div style={containerStyle} role="dialog" aria-label={t('COOKIE_BANNER_TITLE')} aria-live="polite">

      <div style={headerStyle}>
        <svg
          width="20" height="20" viewBox="0 0 24 24" fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10" fill={C.accent} opacity="0.18" />
          <circle cx="12" cy="12" r="10" stroke={C.accent} strokeWidth="1.5" />
          <circle cx="9"  cy="9.5"  r="1.2" fill={C.accent}  />
          <circle cx="14" cy="8"    r="0.9" fill={C.accent2} />
          <circle cx="15" cy="14"   r="1.2" fill={C.accent}  />
          <circle cx="9"  cy="14.5" r="0.9" fill={C.accent2} />
          <circle cx="12" cy="11"   r="0.9" fill={C.accent3} />
        </svg>
        <p style={titleStyle}>{t('COOKIE_BANNER_TITLE')}</p>
      </div>

      <p style={bodyStyle}>
        {t('COOKIE_BANNER_BODY')}{' '}
        <button
          style={linkStyle}
          onClick={() => openCookiesModal('cookies')}
          aria-label={t('COOKIE_BANNER_POLICY_LINK')}
        >
          {t('COOKIE_BANNER_POLICY_LINK')}
        </button>
        .
      </p>

      <div style={actionsStyle}>
        <button
          style={acceptBtnStyle}
          onClick={accept}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          {t('COOKIE_BANNER_ACCEPT')}
        </button>
        <button
          style={declineBtnStyle}
          onClick={decline}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = C.borderH;
            e.currentTarget.style.color = C.text;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.color = C.muted;
          }}
        >
          {t('COOKIE_BANNER_DECLINE')}
        </button>
      </div>

    </div>
  );
}